import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keeps a call-sheet field responsive while Firestore snapshots lag debounced saves.
 * Skips server→local sync while the user is editing (dirty) so inputs don't rubber-band.
 */
export function useLocalDraftField<T>(
  leadId: string,
  server: T,
  fieldPath: string,
  queueField: (path: string, value: T) => void,
  equals: (a: T, b: T) => boolean = (a, b) => a === b,
): readonly [T, (value: T) => void, (value: T) => void] {
  const [local, setLocal] = useState(server);
  const dirty = useRef(false);

  useEffect(() => {
    setLocal(server);
    dirty.current = false;
  }, [leadId]);

  useEffect(() => {
    if (dirty.current) return;
    setLocal(server);
  }, [server]);

  useEffect(() => {
    if (dirty.current && equals(server, local)) {
      dirty.current = false;
    }
  }, [server, local, equals]);

  const onChange = useCallback(
    (value: T) => {
      dirty.current = true;
      setLocal(value);
      queueField(fieldPath, value);
    },
    [fieldPath, queueField],
  );

  /** Force local state from server (e.g. Fill demo content). */
  const resetLocal = useCallback((value: T) => {
    dirty.current = false;
    setLocal(value);
  }, []);

  return [local, onChange, resetLocal] as const;
}

/** Comma-separated display for string[] Firestore fields. */
export function useLocalCommaList(
  leadId: string,
  server: string[] | undefined,
  fieldPath: string,
  queueField: (path: string, value: string[]) => void,
): readonly [string, (value: string) => void, (chips: string[]) => void] {
  const serverJoined = (server ?? []).join(', ');
  const [local, setLocal] = useState(serverJoined);
  const dirty = useRef(false);

  useEffect(() => {
    setLocal(serverJoined);
    dirty.current = false;
  }, [leadId]);

  useEffect(() => {
    if (dirty.current) return;
    setLocal(serverJoined);
  }, [serverJoined]);

  useEffect(() => {
    const parsed = local
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (dirty.current && parsed.join(', ') === (server ?? []).join(', ')) {
      dirty.current = false;
    }
  }, [server, local]);

  const onChange = useCallback(
    (value: string) => {
      dirty.current = true;
      setLocal(value);
      queueField(
        fieldPath,
        value
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      );
    },
    [fieldPath, queueField],
  );

  const resetLocal = useCallback((chips: string[]) => {
    dirty.current = false;
    setLocal(chips.join(', '));
  }, []);

  return [local, onChange, resetLocal] as const;
}
